import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { Check, X, Crown, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

export function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const subscription = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const features = [
    {
      name: 'Food Items',
      free: '15 items',
      pro: 'Unlimited',
    },
    {
      name: 'Household Members',
      free: '2 members',
      pro: 'Unlimited',
    },
    {
      name: 'Barcode Scans',
      free: '5 per month',
      pro: 'Unlimited',
    },
    {
      name: 'Expiration Reminders',
      free: true,
      pro: true,
    },
    {
      name: 'Email Notifications',
      free: true,
      pro: true,
    },
    {
      name: 'Basic Analytics',
      free: true,
      pro: true,
    },
    {
      name: 'Advanced Analytics',
      free: false,
      pro: true,
    },
    {
      name: 'Money Saved Tracking',
      free: false,
      pro: true,
    },
    {
      name: 'Environmental Impact',
      free: false,
      pro: true,
    },
    {
      name: 'Priority Support',
      free: false,
      pro: true,
    },
  ];

  async function handleUpgrade() {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call Cloud Function to create checkout session
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      
      const result = await createCheckoutSession();
      const data = result.data as { url: string };
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-primary-green transition-colors"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">Back</span>
            </button>
            <div className="flex items-center gap-1 sm:gap-2">
              <img src="/logo.png" alt="FridgeLife" className="w-6 h-6 sm:w-8 sm:h-8" />
              <h1 className="text-base sm:text-xl font-bold text-primary-green">FridgeLife</h1>
            </div>
            {user ? (
              <Link
                to="/dashboard"
                className="text-xs sm:text-sm font-medium text-primary-green hover:underline whitespace-nowrap"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="text-xs sm:text-sm font-medium text-primary-green hover:underline whitespace-nowrap"
              >
                Log In
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 md:px-6 py-8 sm:py-12 md:py-16">
        <div className="max-w-6xl mx-auto overflow-x-hidden">
          {/* Title Section */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 text-sm sm:text-base">
              <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold">Upgrade to Premium</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-3">
              Choose Your Plan
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-3">
              Start saving money and reducing waste today
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto mb-8 sm:mb-12 md:mb-16">
            {/* Error Message */}
            {error && (
              <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}
            
            {/* Free Plan */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-10 border-2 border-gray-200 mt-8">
              <div className="text-center mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Free</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-bold">$0</span>
                  <span className="text-gray-600 text-sm sm:text-base">/month</span>
                </div>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">Perfect for getting started</p>
              </div>

              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green flex-shrink-0" />
                  <span>15 food items maximum</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green flex-shrink-0" />
                  <span>2 household members</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green flex-shrink-0" />
                  <span>5 barcode scans/month</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green flex-shrink-0" />
                  <span>Basic analytics</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400 text-sm sm:text-base">
                  <X className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Advanced analytics</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400 text-sm sm:text-base">
                  <X className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Priority support</span>
                </li>
              </ul>

              {subscription.loading ? (
                <button
                  disabled
                  className="w-full py-2 sm:py-3 rounded-xl font-semibold bg-gray-100 text-gray-400 cursor-not-allowed text-sm sm:text-base"
                >
                  Loading...
                </button>
              ) : subscription.isTrial ? (
                <button
                  disabled
                  className="w-full py-2 sm:py-3 rounded-xl font-semibold bg-gray-100 text-gray-400 cursor-not-allowed text-sm sm:text-base"
                >
                  Free Trial Active
                </button>
              ) : !subscription.isActive ? (
                <button
                  disabled
                  className="w-full py-2 sm:py-3 rounded-xl font-semibold bg-gray-100 text-gray-400 cursor-not-allowed text-sm sm:text-base"
                >
                  Current Plan
                </button>
              ) : (
                <button
                  disabled
                  className="w-full py-2 sm:py-3 rounded-xl font-semibold bg-gray-100 text-gray-400 cursor-not-allowed text-sm sm:text-base"
                >
                  —
                </button>
              )}
            </div>

            {/* Pro Plan */}
            <div className="bg-gradient-to-br from-primary-green to-emerald-600 rounded-2xl shadow-2xl p-6 sm:p-8 md:p-10 border-2 border-primary-green relative mt-8">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-yellow-400 text-yellow-900 px-4 py-1.5 rounded-full text-sm font-bold shadow-md whitespace-nowrap">
                  RECOMMENDED
                </div>
              </div>

              <div className="text-center mb-6 sm:mb-8 text-white mt-2">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Premium</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-bold">$4</span>
                  <span className="text-white/80 text-sm sm:text-base">/month</span>
                </div>
                <p className="text-white/90 mt-2 text-sm sm:text-base">Everything you need to save more</p>
              </div>

              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="font-semibold">Unlimited food items</span>
                </li>
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="font-semibold">Unlimited members</span>
                </li>
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="font-semibold">Unlimited barcode scans</span>
                </li>
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Advanced analytics</span>
                </li>
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Money saved tracking</span>
                </li>
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Environmental impact</span>
                </li>
                <li className="flex items-center gap-2 text-white text-sm sm:text-base">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Priority support</span>
                </li>
              </ul>

              {subscription.isActive && !subscription.isTrial ? (
                <button
                  disabled
                  className="w-full bg-white/20 text-white py-2 sm:py-3 rounded-xl font-semibold cursor-not-allowed text-sm sm:text-base"
                >
                  Current Plan
                </button>
              ) : (
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="w-full bg-white text-primary-green py-2 sm:py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
                      Upgrade to Premium
                    </>
                  )}
                </button>
              )}

              <p className="text-xs text-white/80 text-center mt-2 sm:mt-3">
                Cancel anytime
              </p>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Compare Plans
            </h3>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <div className="min-w-[280px] sm:min-w-0 px-2 sm:px-0">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm w-[45%] sm:w-auto">Feature</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm w-[27.5%] sm:w-auto">Free</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-semibold text-primary-green text-xs sm:text-sm w-[27.5%] sm:w-auto">Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-700 text-xs sm:text-sm leading-tight">{feature.name}</td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                          {typeof feature.free === 'boolean' ? (
                            feature.free ? (
                              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green mx-auto" />
                            ) : (
                              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-gray-600 text-xs sm:text-sm block">{feature.free}</span>
                          )}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                          {typeof feature.pro === 'boolean' ? (
                            feature.pro ? (
                              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green mx-auto" />
                            ) : (
                              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-primary-green font-semibold text-xs sm:text-sm block">{feature.pro}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* FAQ or Testimonials Section */}
          <div className="text-center px-3">
            <p className="text-sm sm:text-base text-gray-600 mb-4 break-words">
              Questions? <a href="mailto:support@fridgelife.app" className="text-primary-green hover:underline">Contact us</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
